// Everything that takes the whole screen, in the order it may cover.
//
// Last in the tree and over everything. Each is `position: fixed`, so none is
// in the flow and none can reflow the canvas — which matters here more than it
// looks, because a canvas that resizes reallocates its drawing buffer and shows
// one black frame.

import { BranchPickerOverlay } from "../BranchPicker";
import { CelebrationOverlay } from "../Celebration";
import type { RunEnding } from "../defeat";
import { RunOverOverlay } from "../RunOver";
import type { useCelebrations } from "../useCelebrations";
import type { SoloBranch } from "../useOpponentDriver";
import { useShellMode } from "./ShellMode";

export function ModalStack({
  party,
  front,
  ended
}: {
  /** The prize queue, and the click that claims one (`useCelebrations`). */
  party: ReturnType<typeof useCelebrations>;
  /** A cleared front, asking which way the board grows (`useOpponentDriver`). */
  front: SoloBranch;
  /** The run is over, or null while it is alive. */
  ended: RunEnding | null;
}) {
  const { onExit, onRestart } = useShellMode();

  return (
    <>
      {/* **Keyed by the prize, not merely rendered for it.** Two can land on
          one turn — a stage boundary *is* the moment a king dies, and a king
          always leaves something — so the queue routinely advances while the
          overlay is mounted. Without a key React reuses the instance and the
          new celebration inherits the old one's `claimed`/`leaving`: the plate
          renders already dismissed, the backdrop has undimmed, and the glow
          sits over the board on its own with nothing to close. Reported as a
          glow that persists after the overlay is closed. */}
      {party.current && (
        <CelebrationOverlay
          key={party.current.id}
          celebration={party.current}
          remaining={party.remaining}
          onClaim={party.claim}
        />
      )}

      {/* **The cleared front, asking which way the board grows** (PLAN §19b).
          It stands where the stage card would have — that card is off wherever
          this is on — and the run is genuinely waiting on it: the board sits in
          mate until a `next-stage` lands, which is why the panel has no
          dismiss. Keyed to the stage, for the reason the celebration is keyed
          to its id: an overlay whose subject can change while it is mounted
          inherits the last one's state if React reuses the instance. */}
      {front.branch && (
        <BranchPickerOverlay
          key={front.branch.stage}
          view={front.branch}
          stage={front.branch.stage}
          // Which of the offers was taken is counted in the engine, where the
          // only copy of the answer is (`OpponentDriver.pick`).
          onPick={front.pick}
        />
      )}

      {/* **The run ending, said out loud.** Losing used to be silent: the save
          was wiped, the status chip turned red, and the only way to find out
          was to notice that nothing worked any more. Behind the celebration,
          because a crown that landed on the turn you were mated is still a
          crown you earned and should be shown taking — and this plate is not
          going anywhere. */}
      {ended && !party.current && (
        <RunOverOverlay ending={ended} onRestart={onRestart} onExit={onExit} />
      )}
    </>
  );
}
